import axios from "axios";
import { message } from "antd";
import {
  getUnitApiBase,
  getApiBaseSix,
  getApiBaseEight,
} from "./BaseUrl";

const ok = (data, msg = "Success") => ({ ok: true, msg, data });
const fail = (msg, status = 0, data = null) => ({
  ok: false,
  msg,
  status,
  data,
});

const stripSlashEnd = (s) => String(s || "").replace(/\/+$/, "");
const ensureSlashStart = (s) =>
  String(s || "").startsWith("/") ? String(s || "") : `/${s}`;

const getUnitCode = () => String(localStorage.getItem("unit_code") || "").trim();

const getToken = () => {
  const direct = localStorage.getItem("token");
  if (direct) return String(direct);

  try {
    const ud = JSON.parse(localStorage.getItem("userData") || "{}");
    return String(ud?.token || ud?.data?.token || "");
  } catch {
    return "";
  }
};

// ✅ v7 unit-based default
const buildUrl = (endpoint) => {
  const unit = getUnitCode();
  const ep = ensureSlashStart(endpoint);

  if (!unit) return "";
  const base = getUnitApiBase(unit); // .../index.php/apiv7/{unit}
  return `${stripSlashEnd(base)}${ep}`;
};

// ✅ root-based URL builder (v6 / v7 / v8) — all use unit
const buildUrlWithRoot = (endpoint, root = "v7") => {
  const unit = getUnitCode();
  const ep = ensureSlashStart(endpoint);

  if (!unit) return "";

  if (root === "v6") {
    const base6 = stripSlashEnd(getApiBaseSix());
    return `${base6}/${unit}${ep}`; // .../apiv6/{unit}/endpoint
  }

  if (root === "v8") {
    const base8 = stripSlashEnd(getApiBaseEight());
    return `${base8}/${unit}${ep}`; // .../apiv8/{unit}/endpoint
  }

  // default v7
  const base7 = stripSlashEnd(getUnitApiBase(unit)); // .../apiv7/{unit}
  return `${base7}${ep}`;
};

const toFormData = (payload = {}) => {
  const fd = new FormData();
  Object.entries(payload).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") fd.append(k, v);
  });
  return fd;
};

const handleBody = (body, statusCode = 200) => {
  if (body && typeof body === "object" && "status" in body) {
    if (body.status) return ok(body, body.message || "Success");
    message.error(body.message || "No Result");
    return fail(body.message || "No Result", statusCode, body);
  }
  return ok(body, "Success");
};

const handleCatch = (err) => {
  const status = err?.response?.status || 0;

  if (!err.response) {
    message.error("Network error / Server not reachable");
    return fail("Network error / Server not reachable", 0, err);
  }

  if (status === 401 || status === 403) {
    message.error("Session expired. Please login again.");
    return fail(
      "Session expired. Please login again.",
      status,
      err?.response?.data
    );
  }

  if (status === 404) {
    message.error("API not found (404)");
    return fail("API not found (404)", status, err?.response?.data);
  }

  if (status >= 500) {
    message.error("Server error. Try again later.");
    return fail("Server error. Try again later.", status, err?.response?.data);
  }

  const msg = err?.response?.data?.message || err?.message || "Request failed";
  message.error(msg);
  return fail(msg, status, err?.response?.data);
};

const guardUnit = () => {
  const unit = getUnitCode();
  if (!unit) {
    message.error("Unit code missing. Please login again.");
    return false;
  }
  return true;
};

// ✅ existing (v7)
export const apiGet = async (endpoint, params = {}) => {
  if (!guardUnit()) return fail("Unit code missing", 0, null);

  const url = buildUrl(endpoint);
  const token = getToken();

  try {
    const res = await axios.get(url, {
      params,
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
    return handleBody(res.data, res.status);
  } catch (err) {
    return handleCatch(err);
  }
};

// ✅ existing (v7)
export const apiPostForm = async (endpoint, payload = {}) => {
  if (!guardUnit()) return fail("Unit code missing", 0, null);

  const url = buildUrl(endpoint);
  const token = getToken();

  try {
    const res = await axios.post(url, toFormData(payload), {
      headers: {
        "Content-Type": "multipart/form-data",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    return handleBody(res.data, res.status);
  } catch (err) {
    return handleCatch(err);
  }
};

// ✅ root-based GET (v6 / v7 / v8)
export const apiGetRoot = async (endpoint, root = "v7", params = {}) => {
  if (!guardUnit()) return fail("Unit code missing", 0, null);

  const url = buildUrlWithRoot(endpoint, root);
  const token = getToken();

  try {
    const res = await axios.get(url, {
      params,
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
    return handleBody(res.data, res.status);
  } catch (err) {
    return handleCatch(err);
  }
};

// ✅ root-based POST FormData (v6 / v7 / v8)
export const apiPostFormRoot = async (endpoint, root = "v7", payload = {}) => {
  if (!guardUnit()) return fail("Unit code missing", 0, null);

  const url = buildUrlWithRoot(endpoint, root);
  const token = getToken();

  try {
    const res = await axios.post(url, toFormData(payload), {
      headers: {
        "Content-Type": "multipart/form-data",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    return handleBody(res.data, res.status);
  } catch (err) {
    return handleCatch(err);
  }
};